import {
  isEmail as isEmailExternal,
  isFloat as isFloatExternal,
  isNotDuplicateMail as isNotDuplicateMailExternal,
  isNumber as isNumberExternal,
  isPhoneNumberWithoutSpecialCharacters as isPhoneNumberWithoutSpecialCharactersExternal,
  isValidDate as isValidDateExternal,
  notNone as notNoneExternal,
} from "./validationRules";
import * as z from "zod";
import PasswordValidator from "password-validator";
import { electronicFormatIBAN, isValidIBAN } from "ibantools";
import validator from "validator";
import { apiPost } from "./Api/apiCall";
import _ from "lodash";
import { ProductUnion } from "../types/ProductUnion";
import { hasCreditPayment } from "./accountingService";
import { translate } from "./Translations/translatorService";
import { grossValueToNet } from "./formServiceFunctions";
import { debounceWithReturnedPromise } from "./utils";
import { getEditorHtml } from "./Editor/editorFunctions";
import { Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

const allowEmpty = <T extends z.ZodTypeAny>(schema: T) => {
  // @ts-ignore
  return schema.or(z.literal("").nullish());
};

export const isPastDate = (errorMessage?: string) => {
  return allowEmpty(
    z.coerce.date().refine((date) => date < new Date(), {
      message: errorMessage || "Das Datum muss in der Vergangenheit liegen",
    })
  );
};

export const isFutureDate = (errorMessage?: string) => {
  return allowEmpty(
    z.coerce.date().refine((date) => date > new Date(), {
      message: errorMessage || "Das Datum muss in der Zukunft liegen",
    })
  );
};

export const isTodayOrFutureDate = (errorMessage?: string) => {
  return allowEmpty(
    z.coerce.date().refine(
      (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const inputDate = new Date(date);
        inputDate.setHours(0, 0, 0, 0);

        return inputDate >= today;
      },
      {
        message: errorMessage || "Das Datum darf nicht in der Vergangenheit liegen",
      }
    )
  );
};

export const isBiggerOrSameDateThan = (referenceDate: Date, relatedFieldLabel: string) => {
  return allowEmpty(
    z.coerce.date().refine(
      (date) => {
        const refDate = new Date(referenceDate);
        refDate.setHours(0, 0, 0, 0);
        const inputDate = new Date(date);
        inputDate.setHours(0, 0, 0, 0);
        return inputDate >= refDate;
      },
      {
        message: `Das Datum muss nach dem Datum von ${relatedFieldLabel} liegen`,
      }
    )
  );
};

export const isSmallerOrSameDateThan = (referenceDate: Date, relatedFieldLabel: string) => {
  return allowEmpty(
    z.coerce.date().refine(
      (date) => {
        const refDate = new Date(referenceDate);
        refDate.setHours(0, 0, 0, 0);
        const inputDate = new Date(date);
        inputDate.setHours(0, 0, 0, 0);
        return inputDate <= refDate;
      },
      {
        message: `Das Datum muss vor dem Datum von ${relatedFieldLabel} liegen`,
      }
    )
  );
};

export const matchRegexp = (regexp: RegExp, errorMessage = "") => {
  return allowEmpty(z.string().regex(regexp, errorMessage));
};

export const isSafePassword = () => {
  return z.string().refine(
    (value: string) => {
      let schema = new PasswordValidator();
      schema.is().min(8).has().digits().has().letters();
      return schema.validate(value);
    },
    {
      message: "Mindestens 8 Zeichen. Muss Zahlen und Buchstaben enthalten.",
    }
  );
};

export const isBeaSafeId = () => {
  const beaSafeIdPatterns = [
    /^DE\.Justiz\.\w{8}-\w{4}-\w{4}-\w{4}-\w{6}$/,
    /^DE\.Justiz\.\w{8}-\w{4}-\w{4}-\w{4}-\w{12}\.\w{4}$/,
    /^DE\.Justiztest\.\w{8}-\w{4}-\w{4}-\w{4}-\w{12}\.\w{4}$/,
    /^DE\.BRAK(_SPT)?\.\w{8}-\w{4}-\w{4}-\w{4}-\w{12}\.\w{4}$/,
    /^govello-\w{13}-\w{9}$/,
    /^safe-prod-\w{13}-\w{9}$/,
    /^safe-sp1-\w{13}-\w{9}$/,
    /^safe-sp1-\w{13}$/,
  ];

  return allowEmpty(
    z.string().refine((value) => beaSafeIdPatterns.some((pattern) => pattern.test(value)), {
      message: "Dies ist keine gültige beA Safe-ID.",
    })
  );
};

export const isIban = () => {
  return allowEmpty(
    z.string().refine(
      (value) => {
        const convertedIban = electronicFormatIBAN("DE" + value);
        if (!convertedIban) {
          return false;
        }
        return isValidIBAN(convertedIban);
      },
      {
        message: "Die eingegebene IBAN ist nicht korrekt",
      }
    )
  );
};

export const isValidPostalCode = () => {
  return allowEmpty(
    z.string().refine((value) => validator.isPostalCode(value.trim(), "any"), {
      message: "Die Postleitzahl hat ein ungültiges Format",
    })
  );
};

const debouncedDuplicateEmailCheck = debounceWithReturnedPromise(async (email: string) => {
  const emailCheckResponse = await apiPost("check_email", { email: email.toLowerCase() });
  return !emailCheckResponse.customerExists;
}, 1000);

export const isNotDuplicateMail = (originalEmail: string) => {
  return isEmail().refine(
    async (value) => {
      if (value === originalEmail) {
        return true;
      }

      if (!isEmail().safeParse(value).success) {
        return true;
      }

      return debouncedDuplicateEmailCheck(value);
    },
    {
      message: "Diese E-Mail Adresse existiert bereits",
    }
  );
};

const debouncedCreditPaymentCheck = debounceWithReturnedPromise(async (product: ProductUnion) => {
  const hasPayment = await hasCreditPayment(product);
  return !hasPayment;
}, 1000);

//not tested yet
export const insuranceChangeValidator = (product: ProductUnion) => {
  return z.number().refine(
    async (value) => {
      if (value === product.caseInsurance?.insurer?.id) {
        return true;
      }

      return debouncedCreditPaymentCheck(product);
    },
    {
      message: "Die Versicherung kann nicht mehr geändert werden.",
    }
  );
};

export const requiredValidator = (errorMessage?: string) => {
  return z.any().refine((value) => !_.isEmpty(value), { message: errorMessage || "Dies ist ein Pflichtfeld" });
};

export const isIntString = () => {
  return allowEmpty(
    z.coerce
      .number({ message: "Die Eingabe muss eine Zahl sein" })
      .int({ message: "Die Eingabe muss eine Ganzzahl sein" })
  );
};

export const isPhoneNumberWithoutSpecialCharacters = () => {
  return matchRegexp(
    /^0?[1-9]\d+$/,
    "Die Telefonnummer darf keine Sonderzeichen enthalten oder mehrere führende Nullen haben"
  );
};

export const isFloatString = () => {
  return allowEmpty(
    z.coerce.number({ message: "Die Eingabe muss eine Zahl sein" })
  );
};

export const isNonNegativeFloatString = () => {
  return allowEmpty(
    z.coerce
      .number("Die Eingabe muss eine Zahl sein")
      .positive("Die Eingabe muss eine positive Zahl sein")
      .multipleOf(0.01, "Die Eingabe muss eine positive Zahl mit maximal zwei Nachkommastellen sein")
  );
};

export const notNone = () => {
  return z
    .string()
    .min(1, "Bitte treffen Sie eine Auswahl")
    .refine((value) => value !== "none", "Bitte treffen Sie eine Auswahl");
};

export const maxInt = (max: number) => {
  return allowEmpty(
    z.coerce
      .number("Die Eingabe muss eine Zahl sein")
      .int("Die Eingabe muss eine Ganzzahl sein")
      .lte(max, `Der Betrag darf maximal ${max}€ betragen`)
  );
};

export const maxFloat = (max: number, errorMessage?: string) => {
  return allowEmpty(
    z.coerce
      .number("Die Eingabe muss eine Zahl sein")
      .lte(max, errorMessage || `Der Betrag darf maximal ${max}€ betragen`)
  );
};

export const minFloat = (min: number, errorMessage?: string) => {
  return allowEmpty(
    z.coerce.number("Die Eingabe muss eine Zahl sein").gte(min, errorMessage || `Der Betrag muss mindestens ${min}€ betragen`)
  );
};

export const minStringLength = (length: number, errorMessage?: string) => {
  return allowEmpty(z.string().min(length, errorMessage || "Der Text ist zu kurz"));
};

export const requireRelatedFields = (
  fields: string[],
  errorMessage: string
) => {
  return (data: Record<string, any>, ctx: z.RefinementCtx) => {
    const fieldStates = fields.map((field) => _.isEmpty(_.get(data, field)));
    if (fieldStates.some((state) => state) && fieldStates.some((state) => !state)) {
      fields.forEach((field) => {
        ctx.addIssue({
          code: "custom",
          message: errorMessage,
          path: [field],
        });
      });
    }
  };
};

export const requireAtLeastOneField = (
  fields: string[],
  errorMessage: string
) => {
  return (data: Record<string, any>, ctx: z.RefinementCtx) => {
    const fieldStates = fields.map((field) => _.isEmpty(_.get(data, field)));
    if (fieldStates.every((state) => state)) {
      fields.forEach((field) => {
        ctx.addIssue({
          code: "custom",
          message: errorMessage,
          path: [field],
        });
      });
    }
  };
};


export const invoiceNetValueNotOverpaid = (grossField: string, netField: string, rootObject: ProductUnion["productClassName"]) => {
  return (data: Record<string, any>, ctx: z.RefinementCtx) => {
    const grossNum = parseFloat(_.get(data, grossField));
    const netNum = parseFloat(_.get(data, netField));

    if (Number.isNaN(grossNum) || Number.isNaN(netNum)) {
      return;
    }

    if (grossValueToNet(grossNum) > +netNum.toFixed(2)) {
      ctx.addIssue({
        code: "custom",
        message: `Dieser Bruttozahlbetrag ist höher als die Bruttorechnungssumme, die sich aus dem Nettowert aus dem Feld
      "${translate(rootObject + "." + netField + ".label")}" ergibt.`,
        path: [grossField],
      });
    }
  };
};

export const isValidInstituteReference = () => {
  return allowEmpty(
    matchRegexp(
      /^(?=(?:.*\d){4,})[a-zA-ZäöüÄÖÜß0-9\/&.,\-()+ ]*$/,
      "Das Feld darf keine Sonderzeichen enthalten und muss mindestens 4 Ziffern enthalten."
    )
  );
};


export const dateFieldDefault = () => {
  const MINIMUM_DEFAULT_DATE = new Date("0001-01-01T00:00:00.000Z");
  return isBiggerOrSameDateThan(MINIMUM_DEFAULT_DATE, "Wert darf nicht vor dem 01.01.0001 liegen");
};


//remove when we stop passing validators into fields
export const isPhoneNumberWithoutSpecialCharactersDefaultFieldBased = {
  isPhoneNumberWithoutSpecialCharacters: (value: string) =>
    isPhoneNumberWithoutSpecialCharactersExternal(value) ||
    "Die Telefonnummer darf keine Sonderzeichen enthalten oder mehrere führende Nullen haben.",
};

//remove when we stop passing validators into fields
export const isEmailDefaultFieldBased = {
  isEmail: (value: string) => isEmailExternal(value) || "Dies ist keine gültige E-Mail Adresse",
};

//remove when we stop passing validators into fields
export const isDuplicateMailDefaultFieldBased = (originalMail: string) => ({
  duplicateMail: async (value: string) =>
    (await isNotDuplicateMailExternal(value, originalMail)) || "Diese E-Mail Adresse existiert bereits",
});

//remove when we stop passing validators into fields
export const requiredFieldDefaultFieldBased = {
  required: "Dies ist ein Pflichtfeld",
};

//remove when we stop passing validators into fields
export const textFieldDefaultFieldBased = {
  minLength: {
    value: 2,
    message: "Der Text ist zu kurz",
  },
  maxLength: {
    value: 255,
    message: "Der Text ist zu lang",
  },
};

//remove when we stop passing validators into fields
export const longTextFieldDefaultFieldBased = {
  minLength: textFieldDefaultFieldBased.minLength,
};

//remove when we stop passing validators into fields
export const dateFieldDefaultFieldBased = {
  isValidDate: (value: string) => isValidDateExternal(value) || "Das Datum hat ein falsches Format",
};

//remove when we stop passing validators into fields
export const notNoneSelectValidatorDefaultFieldBased = {
  notNone: (value: string) => notNoneExternal(value) || "Bitte treffen Sie eine Auswahl",
};

//remove when we stop passing validators into fields
export const numberFieldDefaultFieldBased = {
  isNumber: (value: string) => isNumberExternal(value) || "Die Eingabe muss eine Ganzzahl sein",
};

//remove when we stop passing validators into fields
export const floatFieldDefaultFieldBased = [
  {
    isFloat: (value: string) =>
      isFloatExternal(value) || "Die Eingabe muss eine Zahl sein und darf maximal zwei Nachkommastellen haben.",
  },
];

export const moneyFieldDefault = () => {
  return matchRegexp(/^-?\d+(\.\d+)*(,\d{1,2})?$/, "Die Eingabe ist kein Währungswert");
};

export const isEmail = () => {
  return z.email("Dies ist keine gültige E-Mail Adresse");
};

export const percentageFieldDefault = () => {
  return allowEmpty(z.coerce
    .number("Die Eingabe muss eine Zahl sein")
    .gte(0, "Die Eingabe muss eine Zahl zwischen 0 und 100 sein")
    .lte(100, "Die Eingabe muss eine Zahl zwischen 0 und 100 sein"));
};

export const textFieldDefault = () => {
  return allowEmpty(z.string("Die Eingabe muss ein Text sein").min(2, {
    message: "Der Text ist zu kurz",
  }));
};

export const faxNumberDefault = () => {
  return matchRegexp(/^[1-9]\d+$/, "Bitte das Format beachten (Nur Ziffern ohne führende Null)");
};

const debouncedCaseReferenceNumberIsUniqueCheck = debounceWithReturnedPromise(async (caseReferenceNumber: string, productId: number) => {
  let payload: { value: string; productId?: number } = {
    value: caseReferenceNumber,
  };
  if (productId) {
    payload.productId = productId;
  }
  const response = await apiPost("/lb/validator/case_insurance/case_reference_number", payload);
  return response.isValid;
}, 1000);

export const caseReferenceNumberIsUnique = (originalValue: string|null, productId: number) => {
  return z.string("Die Eingabe muss Text sein").refine(
    async (value) => {
      if (value === originalValue) {
        return true;
      }

      return debouncedCaseReferenceNumberIsUniqueCheck(value, productId);
    },
    {
      message: "Diese Schadennummer existiert bereits in einem anderen Rechtsfall.",
    }
  );
};

export const tipTapEditorHasContent = () => {
  return z.string("Die Eingabe muss Text sein").refine(
    (value) => {
      return !!getEditorHtml(new Editor({ content: value, extensions: [StarterKit] }), true);
    },
    {
      message: "Bitte geben Sie mindestens 1 Zeichen ein",
    }
  );
};

export const emailArray = () => {
  return z.array(z.object({
    email: isEmail()
  }))
}
